import { useLocation } from 'react-router-dom'

import { Card, Grid, Icon, LargeIcon, Tag } from '@factorialco/deprecated-design-system'
import { AnimatePresence, motion } from 'framer-motion'
import { ListModuleFragment } from 'generated/resources/graphql'

import { useModuleAvailabilities } from 'modules/core/hooks/useModuleAvailabilities'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import useCheckModuleOnTrial from 'modules/marketplace_modules/hooks/useCheckModuleOnTrial'

type ColorValues = 'lime' | 'malibu' | 'grey' | 'tangerine' | 'purple' | 'champagne'

type Props = {
  modules: ListModuleFragment[]
  isPopularModule?: boolean
}

const ModuleCard = ({ modules, isPopularModule = false }: Props) => {
  const { pathname } = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const { moduleAvailabilities } = useModuleAvailabilities()
  const { isModuleOnTrial } = useCheckModuleOnTrial()

  const statusMap: Record<string, { label: string; color: ColorValues }> = {
    'coming soon': { label: i18n.t('marketplace.modules.soon'), color: 'malibu' },
    installed: { label: i18n.t('marketplace.modules.installed'), color: 'lime' },
    new: { label: i18n.t('marketplace.modules.new'), color: 'grey' },
    updated: { label: i18n.t('marketplace.modules.updated'), color: 'tangerine' },
    popular: { label: i18n.t('marketplace.modules.popular'), color: 'purple' },
    on_trial: { label: i18n.t('marketplace.modules.on_trial'), color: 'champagne' },
  }

  const getLabel = (
    badgeLabel?: string | null,
    chargebeeModuleName?: string | null,
    chargebeeModuleId?: string | null
  ) => {
    if (isPopularModule) return statusMap['popular']
    if (isModuleOnTrial(chargebeeModuleId)) return statusMap['on_trial']

    const isModuleAvailable = moduleAvailabilities.filter((obj) => {
      const modulesArray = chargebeeModuleName?.split(',')
      return modulesArray?.includes(obj.id)
    })[0]

    if (isModuleAvailable?.available) return statusMap['installed']

    return badgeLabel && statusMap[badgeLabel] ? { ...statusMap[badgeLabel] } : undefined
  }

  const onClick = (slug: string) => {
    track(trackingEvents.marketplaceModules.visitModuleDetailPage, {
      module_slug: slug,
      from: pathname,
      from_popular_section: isPopularModule,
      upsell_origin: queryParams.get('upsell_origin'),
    })
  }

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  }

  return (
    <AnimatePresence>
      <motion.div layout variants={container} initial="hidden" animate="show">
        <Grid variant="large">
          {modules.map((module) => {
            const tagLabel = getLabel(
              module.statusBadge,
              module.chargebeeModuleName,
              module.chargebeeModuleId
            )
            return (
              <motion.div key={module.name} variants={item}>
                <Card
                  img={module.thumbnailImage!.url!}
                  to={`${module.slug}?pm=${isPopularModule}`}
                  onClick={() => onClick(module.slug)}
                >
                  <Card.Eyebrow>
                    <Icon.Large icon={module.icon as LargeIcon} color="grey800" />
                    <Tag type={tagLabel ? tagLabel.color : 'transparent'} solid>
                      {tagLabel?.label}
                    </Tag>
                  </Card.Eyebrow>
                  <Card.Title>{module.translatedName!}</Card.Title>
                  <Card.Description>{module.shortDescription!}</Card.Description>
                </Card>
              </motion.div>
            )
          })}
        </Grid>
      </motion.div>
    </AnimatePresence>
  )
}

export default ModuleCard
